// ErrorBoundary.tsx — React class error boundary
// Catches any unhandled render/lifecycle exception and shows a recovery screen
// instead of a blank page. Wrap the entire <App> with this in main.tsx.

import { Component, type ReactNode, type ErrorInfo } from 'react';
import * as Sentry from '@sentry/react';

interface Props  { children: ReactNode }
interface State  { hasError: boolean; message: string }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, message: '' };

  static getDerivedStateFromError(err: Error): State {
    return { hasError: true, message: err.message ?? String(err) };
  }

  componentDidCatch(err: Error, info: ErrorInfo) {
    Sentry.captureException(err, {
      contexts: { react: { componentStack: info.componentStack } },
    });
    if (import.meta.env.DEV) {
      console.error('[CV] Unhandled render error:', err, info.componentStack);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, message: '' });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '100vh', gap: 16,
        background: '#0d1b2a', color: '#e0e6ee', fontFamily: 'monospace',
      }}>
        <div style={{ fontSize: 48 }}>⚠️</div>
        <h2 style={{ margin: 0 }}>Something went wrong</h2>
        <p style={{ color: '#778899', maxWidth: 480, textAlign: 'center', margin: 0 }}>
          {this.state.message || 'An unexpected error occurred.'}
        </p>
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={this.handleReset}
            style={{ padding: '8px 20px', background: '#1e3a5f', border: '1px solid #3a6ea0', color: '#e0e6ee', cursor: 'pointer', borderRadius: 4 }}
          >
            Try Again
          </button>
          <button
            onClick={() => { localStorage.clear(); location.reload(); }}
            style={{ padding: '8px 20px', background: '#3a1a1a', border: '1px solid #7a3a3a', color: '#e0e6ee', cursor: 'pointer', borderRadius: 4 }}
          >
            Clear Data &amp; Reload
          </button>
        </div>
        {import.meta.env.DEV && (
          <pre style={{ fontSize: 11, color: '#556677', maxWidth: 600, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
            {this.state.message}
          </pre>
        )}
      </div>
    );
  }
}
